import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

const ListsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [lists, setLists] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState('recent'); // 'recent', 'name-asc', 'name-desc'

  useEffect(() => {
    fetchLists();
  }, []);

  const fetchLists = async () => {
    try {
      const response = await api.get('/lists');
      setLists(response.data);
    } catch (err) {
      setError(err.message || t('errors.serverError'));
    } finally {
      setIsLoading(false);
    }
  };

  const filteredLists = lists
    .filter((list) => list.name?.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => {
      if (sortOrder === 'name-asc') {
        return (a.name || '').localeCompare(b.name || '');
      } else if (sortOrder === 'name-desc') {
        return (b.name || '').localeCompare(a.name || '');
      }
      // 'recent' - keep original order (lastUsedAt DESC from API)
      return 0;
    });

  // Find the most recently used list (first one with lastUsedAt since API orders by lastUsedAt DESC)
  const lastUsedListId = lists.find(list => list.lastUsedAt)?.id || null;

  if (isLoading) {
    return (
      <div className="app-loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">{t('lists.title')}</h1>
      </div>

      {error && (
        <div className="form-error" style={{ marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      {/* Search and actions */}
      <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        <input
          type="search"
          className="form-input"
          placeholder={t('common.search')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ flex: 1, minWidth: '200px' }}
        />
        <button
          className="btn btn-primary"
          onClick={() => navigate('/lists/new')}
        >
          {t('lists.create')}
        </button>
      </div>

      {/* Import buttons and sort */}
      <div style={{ marginBottom: '1.5rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <button
          className="btn btn-outline"
          onClick={() => navigate('/import/csv')}
        >
          {t('lists.importCsv')}
        </button>
        <button
          className="btn btn-outline"
          onClick={() => navigate('/import/qr')}
        >
          {t('lists.importQr')}
        </button>
        <select
          className="form-input"
          value={sortOrder}
          onChange={(e) => setSortOrder(e.target.value)}
          style={{ marginLeft: 'auto', minWidth: '150px' }}
          aria-label={t('common.sort') || 'Sort'}
        >
          <option value="recent">{t('lists.sortRecent') || 'Recently used'}</option>
          <option value="name-asc">{t('lists.sortNameAsc') || 'Name (A-Z)'}</option>
          <option value="name-desc">{t('lists.sortNameDesc') || 'Name (Z-A)'}</option>
        </select>
      </div>

      {/* Lists */}
      {filteredLists.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
              <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
            </svg>
          </div>
          <h2 className="empty-state-title">
            {searchTerm ? t('common.noResults') : t('lists.empty')}
          </h2>
          {!searchTerm && (
            <p>{t('lists.createFirst')}</p>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {filteredLists.map((list) => {
            const isLastUsed = list.id === lastUsedListId;
            return (
              <Link
                key={list.id}
                to={`/lists/${list.id}`}
                className={`card card-clickable${isLastUsed ? ' card-last-used' : ''}`}
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <h3 className="card-title" style={{ marginBottom: '0.25rem' }}>
                      {list.name}
                      {isLastUsed && (
                        <span className="last-used-badge">
                          {t('lists.lastUsed')}
                        </span>
                      )}
                      {list.isQrImported && (
                        <span className="badge" style={{ marginLeft: '0.5rem', fontSize: '0.7rem' }}>
                          QR
                        </span>
                      )}
                    </h3>
                    <p className="card-subtitle">
                      {list.language1} / {list.language2}
                    </p>
                    <p className="card-subtitle" style={{ marginTop: '0.25rem' }}>
                      {list.vocabCount || 0} {t('lists.vocabularies')}
                    </p>
                  </div>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="9 18 15 12 9 6"></polyline>
                  </svg>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ListsPage;
